<!--
  Image locks: who holds what, and how to release them.

  Why it lives in the work queue rather than in Settings
  -----------------------------------------------------
  A lock is a fact about *work*, not about configuration. The operator discovers it here — the
  queue is where they choose what to open and where "Annotate" is about to answer 409 — and the
  rows this panel describes are the rows immediately below it. Settings is where the bucket and
  the credentials live: things set once and rarely touched, by contrast with a lock that is
  cleared mid-session precisely because something went wrong a minute ago. Putting the control
  two clicks away from the failure it fixes would be the wrong trade.

  The panel renders **only when there are locks**, so it costs nothing when there is nothing to
  say — and it is impossible to miss when there is.

  Force-clearing is never one click
  ---------------------------------
  "Release stale locks" acts immediately, because a stale lock is already reclaimable by any
  annotator through the ordinary acquire path: releasing it changes nothing anybody could be
  relying on. Releasing a **live** lock takes an image away from somebody who may have it open
  right now, so it goes through a confirmation that states plainly how many are live, how many
  of those belong to somebody else, and names the images. The service refuses the request
  without `confirm_release_live` in any case; this is the half the operator can read.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import type { ImageLockOut } from '../lib/api/types';
  import { href } from '../lib/router.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { locks } from '../lib/stores/locks.svelte';

  interface Props {
    /** Reload the queue after a clear, so the rows and the locks cannot disagree. */
    onchanged?: () => void;
  }

  const { onchanged }: Props = $props();

  let confirming = $state(false);
  let reason = $state('');

  $effect(() => {
    untrack(() => {
      if (!locks.inventory && !locks.loading) void locks.load();
    });
  });

  function ageLabel(item: ImageLockOut): string {
    // `locked_by` with no `locked_at` cannot be aged, so it would never expire on its own.
    // Every other code path already reads it as unlocked; here it is a row to explain, not to
    // paper over with a made-up age.
    if (!item.lock.locked_at) return 'no timestamp — it can never expire on its own';
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(item.lock.locked_at)) / 60000));
    if (!item.lock.held) return `expired — taken ${minutes} min ago`;
    const expiry = item.lock.expires_at ? Date.parse(item.lock.expires_at) : NaN;
    return Number.isFinite(expiry)
      ? `held for ${minutes} min, expires ${new Date(expiry).toLocaleTimeString()}`
      : `held for ${minutes} min`;
  }

  async function clearStale(): Promise<void> {
    await locks.clear({ scope: 'stale', confirmReleaseLive: false, reason: reason || undefined });
    onchanged?.();
  }

  async function clearAll(): Promise<void> {
    // `confirmReleaseLive: true` is written out here, at the one call site that sits behind the
    // confirmation above. Nothing derives it from the scope.
    await locks.clear({ scope: 'all', confirmReleaseLive: true, reason: reason || undefined });
    confirming = false;
    reason = '';
    onchanged?.();
  }
</script>

{#if locks.total > 0 || locks.problem || locks.lastResult}
  <div class="panel col">
    <div class="row wrap">
      <h2>Image locks</h2>
      <span class="spacer"></span>
      <button class="subtle small" onclick={() => void locks.load()} disabled={locks.loading}>
        Refresh
      </button>
    </div>

    {#if locks.total > 0}
      <p class="small">
        <strong>{locks.total}</strong> image(s) locked ·
        <span class="tag live">{locks.live} live</span>
        <span class="tag stale">{locks.stale} stale</span>
        <span class="muted">
          — a lock goes stale after {Math.round((locks.inventory?.ttl_seconds ?? 900) / 60)} minutes
          and is then reclaimable by anyone. Live means somebody may have the image open right now.
        </span>
      </p>

      <table class="small">
        <thead>
          <tr>
            <th>Image</th>
            <th>Held by</th>
            <th>State</th>
            <th>Boxes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each locks.locks as item (item.image_id)}
            <tr>
              <td>
                <a href={href({ name: 'image', imageId: item.image_id })}>
                  {item.siglum} <span class="muted mono">#{item.image_id}</span>
                </a>
              </td>
              <td>
                {item.lock.locked_by_name ?? item.lock.locked_by ?? 'unknown'}
                {#if item.held_by_caller}<span class="tag mine">you</span>{/if}
              </td>
              <td>
                <!-- Never colour alone: each state carries its own word. -->
                <span class="tag" class:live={item.lock.held} class:stale={!item.lock.held}>
                  {item.lock.held ? 'live' : 'stale'}
                </span>
                <span class="muted">{ageLabel(item)}</span>
              </td>
              <td class="mono">{item.annotation_count}</td>
              <td class="mono muted">{item.status}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      {#if locks.inventory?.truncated}
        <p class="small muted">
          Showing {locks.inventory.returned} of {locks.total}. Clearing still considers every lock.
        </p>
      {/if}
    {/if}

    {#if locks.problem}
      <div class="banner error small">
        <strong>{locks.problem.code}</strong>
        <p>{locks.problem.message}</p>
      </div>
    {/if}

    {#if locks.lastResult}
      <div class="banner {locks.lastResult.live_released ? 'warn' : 'ok'} small col">
        <strong>
          Released {locks.lastResult.released_count} lock(s):
          {locks.lastResult.live_released} live, {locks.lastResult.stale_released} stale.
        </strong>
        {#if locks.lastResult.released.length}
          <ul>
            {#each locks.lastResult.released as item (item.image_id)}
              <li>
                {item.siglum} <span class="mono muted">#{item.image_id}</span> — was held by
                {item.previous_holder_name}
                {item.was_live ? '(live)' : '(stale)'}, {item.annotation_count} box(es), which are
                untouched.
              </li>
            {/each}
          </ul>
        {/if}
        {#if locks.lastResult.retained.length}
          <p>
            {locks.lastResult.retained.length} live lock(s) were left alone. Use "release live
            locks" if you are certain you are the only active annotator.
          </p>
        {/if}
        <div class="row">
          <button class="subtle" onclick={() => locks.dismissResult()}>Dismiss</button>
        </div>
      </div>
    {/if}

    {#if !annotator.ready}
      <p class="small muted">
        Clearing a lock records who cleared it, so it needs an annotator id like every other
        write.
      </p>
    {:else if confirming}
      <div class="banner warn small col">
        <strong>
          Release {locks.total} lock(s), of which {locks.live} are LIVE
          {#if locks.liveHeldByOthers}
            and {locks.liveHeldByOthers} belong to someone other than you
          {/if}?
        </strong>
        <p>
          A live lock means that annotator may have the image open right now. Releasing it lets
          somebody else claim the same image, and the bulk-upsert write path is
          last-writer-wins — so the other annotator can lose boxes they have not saved. Do this
          only when you know you are the only active writer.
        </p>
        <p class="muted">
          It is recorded: the service logs each forced release with both names and the expiry it
          cut short. No annotation is changed — only the lock columns.
        </p>
        <label class="col">
          Reason (goes in the service log)
          <input type="text" bind:value={reason} placeholder="only active user; tab crashed" />
        </label>
        <div class="row wrap">
          <button class="danger" onclick={() => void clearAll()} disabled={locks.clearing}>
            {locks.clearing ? 'Releasing…' : `Yes, release all ${locks.total}`}
          </button>
          <button class="subtle" onclick={() => (confirming = false)} disabled={locks.clearing}>
            Cancel
          </button>
        </div>
      </div>
    {:else if locks.total > 0}
      <div class="row wrap">
        <button onclick={() => void clearStale()} disabled={locks.clearing || locks.stale === 0}>
          Release {locks.stale} stale lock(s)
        </button>
        <button class="subtle" onclick={() => (confirming = true)} disabled={locks.clearing}>
          Release live locks too…
        </button>
        <span class="small muted">
          Stale locks are already reclaimable by anyone; releasing them is safe. Live ones need a
          confirmation.
        </span>
      </div>
    {/if}
  </div>
{/if}

<style>
  .tag {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 5px;
    margin-right: 4px;
    font-size: var(--text-xs);
  }

  .tag.live {
    border-color: var(--warn);
    color: var(--warn);
  }

  .tag.stale {
    border-color: var(--line-strong);
    color: var(--text-dim);
  }

  .tag.mine {
    border-color: var(--accent);
    color: var(--accent);
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  th {
    text-align: left;
  }

  p {
    margin: 2px 0;
  }
</style>
